import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";
import { all_routes } from "../../router/all_routes";

interface SyncTaskState {
  status: "IDLE" | "SYNCING" | "SUCCESS" | "FAILED";
  message?: string;
  responsePayload?: any;
}

const ReimbursementSync: React.FC = () => {
  const routes = all_routes;

  // Logged-in user fallback
  const [loggedInEmpId, setLoggedInEmpId] = useState<string>("");

  // Individual Emp ID inputs for each API
  const [empIds, setEmpIds] = useState<{ [key: string]: string }>({
    syncMasters: "",
    syncRequests: "",
    syncTypes: "",
    syncLtcEligibility: "",
    syncEligibility: "",
    syncData: "",
    syncRequestById: "",
  });

  // Additional specific parameters
  const [masterType, setMasterType] = useState<string>("All");
  const [referenceNumber, setReferenceNumber] = useState<string>("");

  // Read logged-in user on mount & initialize all empId states with loggedInEmpId
  useEffect(() => {
    try {
      const userJson = JSON.parse(localStorage.getItem("user") || "{}");
      const currentEmpId =
        userJson?.details?.empId ||
        userJson?.empId ||
        userJson?.usrid ||
        "";
      if (currentEmpId) {
        const empIdStr = String(currentEmpId);
        setLoggedInEmpId(empIdStr);
        setEmpIds({
          syncMasters: empIdStr,
          syncRequests: empIdStr,
          syncTypes: empIdStr,
          syncLtcEligibility: empIdStr,
          syncEligibility: empIdStr,
          syncData: empIdStr,
          syncRequestById: empIdStr,
        });
      }
    } catch (e) {
      console.error("Error reading logged in user:", e);
    }
  }, []);

  // Response Payload Modal State
  const [activeResponseModal, setActiveResponseModal] = useState<{
    title: string;
    data: any;
  } | null>(null);

  // States for sync tasks
  const [tasks, setTasks] = useState<{ [key: string]: SyncTaskState }>({
    syncMasters: { status: "IDLE" },
    syncRequests: { status: "IDLE" },
    syncTypes: { status: "IDLE" },
    syncLtcEligibility: { status: "IDLE" },
    syncEligibility: { status: "IDLE" },
    syncData: { status: "IDLE" },
    syncRequestById: { status: "IDLE" },
  });

  const updateTaskState = (key: string, patch: Partial<SyncTaskState>) => {
    setTasks((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        ...patch,
      },
    }));
  };

  // Helper to resolve empId for a key
  const getEmpIdForKey = (key: string) => {
    const val = empIds[key];
    if (val && val.trim()) return val.trim();
    return loggedInEmpId.trim();
  };

  // 1. Sync Masters
  const handleSyncMasters = async () => {
    const key = "syncMasters";
    updateTaskState(key, { status: "SYNCING" });
    try {
      const targetEmpId = getEmpIdForKey(key);
      const payload: any = { type: masterType || "All" };
      if (targetEmpId) payload.empId = targetEmpId;

      const res = await Api.post(API_URLS.reimbursement.SYNC_MASTERS, payload);
      updateTaskState(key, {
        status: "SUCCESS",
        message: res?.message || "Masters synchronized successfully",
        responsePayload: res,
      });
      toast.success("Masters synchronized successfully!");
    } catch (err: any) {
      const errorMsg = Api.getErrorMessage(err);
      updateTaskState(key, {
        status: "FAILED",
        message: errorMsg,
        responsePayload: err?.response?.data || { error: errorMsg },
      });
      toast.error(`Masters Sync Failed: ${errorMsg}`);
    }
  };

  // 2. Sync Requests
  const handleSyncRequests = async () => {
    const key = "syncRequests";
    const targetEmpId = getEmpIdForKey(key);
    if (!targetEmpId) {
      toast.warning("Please enter an Employee ID for Reimbursement Requests sync.");
      return;
    }
    updateTaskState(key, { status: "SYNCING" });
    try {
      const payload = { empId: targetEmpId };
      const res = await Api.post(API_URLS.reimbursement.SYNC_REIMBURSEMENT_REQUEST, payload);
      updateTaskState(key, {
        status: "SUCCESS",
        message: res?.message || "Requests synchronized successfully",
        responsePayload: res,
      });
      toast.success("Requests synchronized successfully!");
    } catch (err: any) {
      const errorMsg = Api.getErrorMessage(err);
      updateTaskState(key, {
        status: "FAILED",
        message: errorMsg,
        responsePayload: err?.response?.data || { error: errorMsg },
      });
      toast.error(`Requests Sync Failed: ${errorMsg}`);
    }
  };

  // 3. Sync Types
  const handleSyncTypes = async () => {
    const key = "syncTypes";
    const targetEmpId = getEmpIdForKey(key);
    if (!targetEmpId) {
      toast.warning("Please enter an Employee ID for Reimbursement Types sync.");
      return;
    }
    updateTaskState(key, { status: "SYNCING" });
    try {
      const payload = { empId: targetEmpId };
      const res = await Api.post(API_URLS.reimbursement.SYNC_REIMBURSEMENT_TYPES, payload);
      updateTaskState(key, {
        status: "SUCCESS",
        message: res?.message || "Types synchronized successfully",
        responsePayload: res,
      });
      toast.success("Types synchronized successfully!");
    } catch (err: any) {
      const errorMsg = Api.getErrorMessage(err);
      updateTaskState(key, {
        status: "FAILED",
        message: errorMsg,
        responsePayload: err?.response?.data || { error: errorMsg },
      });
      toast.error(`Types Sync Failed: ${errorMsg}`);
    }
  };

  // 4. Sync LTC Eligibility
  const handleSyncLtcEligibility = async () => {
    const key = "syncLtcEligibility";
    const targetEmpId = getEmpIdForKey(key);
    if (!targetEmpId) {
      toast.warning("Please enter an Employee ID for LTC Eligibility sync.");
      return;
    }
    updateTaskState(key, { status: "SYNCING" });
    try {
      const payload = { empId: targetEmpId };
      const res = await Api.post(API_URLS.reimbursement.LTC_ELIGIBILITY, payload);
      updateTaskState(key, {
        status: "SUCCESS",
        message: res?.message || "LTC eligibility synchronized successfully",
        responsePayload: res,
      });
      toast.success("LTC eligibility synchronized successfully!");
    } catch (err: any) {
      const errorMsg = Api.getErrorMessage(err);
      updateTaskState(key, {
        status: "FAILED",
        message: errorMsg,
        responsePayload: err?.response?.data || { error: errorMsg },
      });
      toast.error(`LTC Sync Failed: ${errorMsg}`);
    }
  };

  // 5. Sync General Eligibility
  const handleSyncEligibility = async () => {
    const key = "syncEligibility";
    const targetEmpId = getEmpIdForKey(key);
    updateTaskState(key, { status: "SYNCING" });
    try {
      const payload = targetEmpId ? { empId: targetEmpId } : {};
      const res = await Api.post(API_URLS.reimbursement.SYNC_ELIGIBILITY, payload);
      updateTaskState(key, {
        status: "SUCCESS",
        message: res?.message || "Eligibility synchronized successfully",
        responsePayload: res,
      });
      toast.success("Eligibility synchronized successfully!");
    } catch (err: any) {
      const errorMsg = Api.getErrorMessage(err);
      updateTaskState(key, {
        status: "FAILED",
        message: errorMsg,
        responsePayload: err?.response?.data || { error: errorMsg },
      });
      toast.error(`Eligibility Sync Failed: ${errorMsg}`);
    }
  };

  // 6. Sync Data
  const handleSyncData = async () => {
    const key = "syncData";
    const targetEmpId = getEmpIdForKey(key);
    if (!targetEmpId) {
      toast.warning("Please enter an Employee ID for Data Sync.");
      return;
    }
    updateTaskState(key, { status: "SYNCING" });
    try {
      const payload = { empId: targetEmpId };
      const res = await Api.post(API_URLS.reimbursement.SYNC_DATA, payload);
      updateTaskState(key, {
        status: "SUCCESS",
        message: res?.message || "Reimbursement data synchronized successfully",
        responsePayload: res,
      });
      toast.success("Reimbursement data synchronized successfully!");
    } catch (err: any) {
      const errorMsg = Api.getErrorMessage(err);
      updateTaskState(key, {
        status: "FAILED",
        message: errorMsg,
        responsePayload: err?.response?.data || { error: errorMsg },
      });
      toast.error(`Sync Data Failed: ${errorMsg}`);
    }
  };

  // 7. Sync Request By Reference Number
  const handleSyncRequestById = async () => {
    const key = "syncRequestById";
    if (!referenceNumber.trim()) {
      toast.warning("Please enter a Reference Number.");
      return;
    }
    updateTaskState(key, { status: "SYNCING" });
    try {
      const targetEmpId = getEmpIdForKey(key);
      const payload: any = { referenceNumber: referenceNumber.trim() };
      if (targetEmpId) payload.empId = targetEmpId;

      const res = await Api.post(API_URLS.reimbursement.REIMBURSEMENT_REQUEST_BY_ID, payload);
      updateTaskState(key, {
        status: "SUCCESS",
        message: res?.message || "Request details fetched successfully",
        responsePayload: res,
      });
      toast.success("Request details fetched successfully!");
    } catch (err: any) {
      const errorMsg = Api.getErrorMessage(err);
      updateTaskState(key, {
        status: "FAILED",
        message: errorMsg,
        responsePayload: err?.response?.data || { error: errorMsg },
      });
      toast.error(`Request by ID Failed: ${errorMsg}`);
    }
  };

  // Bulk Sync All
  const handleSyncAll = async () => {
    toast.info("Starting synchronization of all reimbursement components...");
    await handleSyncMasters();
    await handleSyncRequests();
    await handleSyncTypes();
    await handleSyncLtcEligibility();
    await handleSyncEligibility();
    await handleSyncData();
  };

  const getStatusBadge = (status: SyncTaskState["status"]) => {
    switch (status) {
      case "SYNCING":
        return <span className="badge bg-soft-primary text-primary px-3 py-1 rounded-pill"><i className="ti ti-refresh spinning me-1"></i> SYNCING...</span>;
      case "SUCCESS":
        return <span className="badge bg-soft-success text-success px-3 py-1 rounded-pill"><i className="ti ti-check me-1"></i> SUCCESS</span>;
      case "FAILED":
        return <span className="badge bg-soft-danger text-danger px-3 py-1 rounded-pill"><i className="ti ti-alert-circle me-1"></i> FAILED</span>;
      default:
        return <span className="badge bg-light text-muted px-3 py-1 rounded-pill">IDLE</span>;
    }
  };

  return (
    <div className="page-wrapper bg-white">
      <style>{`
        .sync-item {
          border: 1px solid #e9ecef;
          border-radius: 8px;
          background: #fff;
          transition: background 0.2s;
        }
        .sync-item:hover {
          background: #f8f9fa;
        }
        .icon-box-lite {
          width: 42px;
          height: 42px;
          border-radius: 8px;
          background: #f1f3f5;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #495057;
          font-size: 20px;
        }
        .bg-soft-primary { background-color: #e7f5ff; }
        .bg-soft-success { background-color: #ebfbee; }
        .bg-soft-danger { background-color: #fff5f5; }
        @keyframes rotating {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spinning { animation: rotating 2s linear infinite; }
      `}</style>

      <div className="content">
        {/* Standard Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Reimbursement Sync</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.reimbursementAdmin}>Services</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Reimbursement Sync
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <button
              className="btn btn-primary d-flex align-items-center"
              onClick={handleSyncAll}
            >
              <i className="ti ti-refresh me-2"></i> Sync All Components
            </button>
          </div>
        </div>

        {/* Sync Operations List */}
        <div className="d-flex flex-column gap-3 mb-4">
          {/* 1. Sync Masters */}
          <div className="sync-item p-3">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-2">
              <div className="d-flex align-items-center gap-3">
                <div className="icon-box-lite">
                  <i className={`ti ti-database ${tasks.syncMasters.status === "SYNCING" ? "spinning text-primary" : ""}`}></i>
                </div>
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <h6 className="fw-bold mb-0 text-dark">1. Sync Masters</h6>
                    {getStatusBadge(tasks.syncMasters.status)}
                  </div>
                  <small className="text-muted">Synchronize master tables, cascading configuration, and reimbursement types.</small>
                </div>
              </div>
            </div>

            <div className="row align-items-center g-2 mt-2 pt-2 border-top">
              <div className="col-md-4">
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-light text-muted fw-medium" style={{ fontSize: "12px" }}>Master Type</span>
                  <select
                    className="form-select"
                    value={masterType}
                    onChange={(e) => setMasterType(e.target.value)}
                  >
                    <option value="All">All Masters</option>
                    <option value="Claims">Claims</option>
                    <option value="Types">Types</option>
                    <option value="Cascading">Cascading</option>
                  </select>
                </div>
              </div>
              <div className="col-md-4">
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-light text-muted fw-medium" style={{ fontSize: "12px" }}>Emp ID</span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Employee ID (Optional)"
                    value={empIds.syncMasters}
                    onChange={(e) => setEmpIds((prev) => ({ ...prev, syncMasters: e.target.value }))}
                  />
                </div>
              </div>
              <div className="col-md-auto ms-auto d-flex gap-2">
                {tasks.syncMasters.responsePayload && (
                  <button
                    className="btn btn-outline-secondary btn-sm rounded-pill px-3"
                    onClick={() => setActiveResponseModal({ title: "Sync Masters Response", data: tasks.syncMasters.responsePayload })}
                  >
                    Response
                  </button>
                )}
                <button
                  className="btn btn-outline-primary btn-sm rounded-pill px-4 fw-bold"
                  onClick={handleSyncMasters}
                  disabled={tasks.syncMasters.status === "SYNCING"}
                >
                  {tasks.syncMasters.status === "SYNCING" ? "Syncing..." : "Sync Now"}
                </button>
              </div>
            </div>
          </div>

          {/* 2. Sync Requests */}
          <div className="sync-item p-3">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-2">
              <div className="d-flex align-items-center gap-3">
                <div className="icon-box-lite">
                  <i className={`ti ti-file-text ${tasks.syncRequests.status === "SYNCING" ? "spinning text-primary" : ""}`}></i>
                </div>
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <h6 className="fw-bold mb-0 text-dark">2. Sync Reimbursement Requests</h6>
                    {getStatusBadge(tasks.syncRequests.status)}
                  </div>
                  <small className="text-muted">Retrieve employee reimbursement claims & request status updates from SAP.</small>
                </div>
              </div>
            </div>

            <div className="row align-items-center g-2 mt-2 pt-2 border-top">
              <div className="col-md-5">
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-light text-muted fw-medium" style={{ fontSize: "12px" }}>Emp ID</span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Employee ID e.g. 22088"
                    value={empIds.syncRequests}
                    onChange={(e) => setEmpIds((prev) => ({ ...prev, syncRequests: e.target.value }))}
                  />
                </div>
              </div>
              <div className="col-md-auto ms-auto d-flex gap-2">
                {tasks.syncRequests.responsePayload && (
                  <button
                    className="btn btn-outline-secondary btn-sm rounded-pill px-3"
                    onClick={() => setActiveResponseModal({ title: "Sync Requests Response", data: tasks.syncRequests.responsePayload })}
                  >
                    Response
                  </button>
                )}
                <button
                  className="btn btn-outline-primary btn-sm rounded-pill px-4 fw-bold"
                  onClick={handleSyncRequests}
                  disabled={tasks.syncRequests.status === "SYNCING"}
                >
                  {tasks.syncRequests.status === "SYNCING" ? "Syncing..." : "Sync Now"}
                </button>
              </div>
            </div>
          </div>

          {/* 3. Sync Types */}
          <div className="sync-item p-3">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-2">
              <div className="d-flex align-items-center gap-3">
                <div className="icon-box-lite">
                  <i className={`ti ti-category ${tasks.syncTypes.status === "SYNCING" ? "spinning text-primary" : ""}`}></i>
                </div>
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <h6 className="fw-bold mb-0 text-dark">3. Sync Reimbursement Types</h6>
                    {getStatusBadge(tasks.syncTypes.status)}
                  </div>
                  <small className="text-muted">Update allowed reimbursement types and limit caps per grade/designation.</small>
                </div>
              </div>
            </div>

            <div className="row align-items-center g-2 mt-2 pt-2 border-top">
              <div className="col-md-5">
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-light text-muted fw-medium" style={{ fontSize: "12px" }}>Emp ID</span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Employee ID e.g. 22088"
                    value={empIds.syncTypes}
                    onChange={(e) => setEmpIds((prev) => ({ ...prev, syncTypes: e.target.value }))}
                  />
                </div>
              </div>
              <div className="col-md-auto ms-auto d-flex gap-2">
                {tasks.syncTypes.responsePayload && (
                  <button
                    className="btn btn-outline-secondary btn-sm rounded-pill px-3"
                    onClick={() => setActiveResponseModal({ title: "Sync Types Response", data: tasks.syncTypes.responsePayload })}
                  >
                    Response
                  </button>
                )}
                <button
                  className="btn btn-outline-primary btn-sm rounded-pill px-4 fw-bold"
                  onClick={handleSyncTypes}
                  disabled={tasks.syncTypes.status === "SYNCING"}
                >
                  {tasks.syncTypes.status === "SYNCING" ? "Syncing..." : "Sync Now"}
                </button>
              </div>
            </div>
          </div>

          {/* 4. Sync LTC Eligibility */}
          <div className="sync-item p-3">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-2">
              <div className="d-flex align-items-center gap-3">
                <div className="icon-box-lite">
                  <i className={`ti ti-plane ${tasks.syncLtcEligibility.status === "SYNCING" ? "spinning text-primary" : ""}`}></i>
                </div>
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <h6 className="fw-bold mb-0 text-dark">4. Sync LTC Eligibility</h6>
                    {getStatusBadge(tasks.syncLtcEligibility.status)}
                  </div>
                  <small className="text-muted">Synchronize Leave Travel Concession (LTC) entitlements and block period data.</small>
                </div>
              </div>
            </div>

            <div className="row align-items-center g-2 mt-2 pt-2 border-top">
              <div className="col-md-5">
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-light text-muted fw-medium" style={{ fontSize: "12px" }}>Emp ID</span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Employee ID e.g. 22088"
                    value={empIds.syncLtcEligibility}
                    onChange={(e) => setEmpIds((prev) => ({ ...prev, syncLtcEligibility: e.target.value }))}
                  />
                </div>
              </div>
              <div className="col-md-auto ms-auto d-flex gap-2">
                {tasks.syncLtcEligibility.responsePayload && (
                  <button
                    className="btn btn-outline-secondary btn-sm rounded-pill px-3"
                    onClick={() => setActiveResponseModal({ title: "LTC Eligibility Response", data: tasks.syncLtcEligibility.responsePayload })}
                  >
                    Response
                  </button>
                )}
                <button
                  className="btn btn-outline-primary btn-sm rounded-pill px-4 fw-bold"
                  onClick={handleSyncLtcEligibility}
                  disabled={tasks.syncLtcEligibility.status === "SYNCING"}
                >
                  {tasks.syncLtcEligibility.status === "SYNCING" ? "Syncing..." : "Sync Now"}
                </button>
              </div>
            </div>
          </div>

          {/* 5. Sync Eligibility */}
          <div className="sync-item p-3">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-2">
              <div className="d-flex align-items-center gap-3">
                <div className="icon-box-lite">
                  <i className={`ti ti-shield-check ${tasks.syncEligibility.status === "SYNCING" ? "spinning text-primary" : ""}`}></i>
                </div>
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <h6 className="fw-bold mb-0 text-dark">5. Sync General Eligibility</h6>
                    {getStatusBadge(tasks.syncEligibility.status)}
                  </div>
                  <small className="text-muted">Refresh general reimbursement limits and quota balances.</small>
                </div>
              </div>
            </div>

            <div className="row align-items-center g-2 mt-2 pt-2 border-top">
              <div className="col-md-5">
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-light text-muted fw-medium" style={{ fontSize: "12px" }}>Emp ID</span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Employee ID (Optional)"
                    value={empIds.syncEligibility}
                    onChange={(e) => setEmpIds((prev) => ({ ...prev, syncEligibility: e.target.value }))}
                  />
                </div>
              </div>
              <div className="col-md-auto ms-auto d-flex gap-2">
                {tasks.syncEligibility.responsePayload && (
                  <button
                    className="btn btn-outline-secondary btn-sm rounded-pill px-3"
                    onClick={() => setActiveResponseModal({ title: "Sync Eligibility Response", data: tasks.syncEligibility.responsePayload })}
                  >
                    Response
                  </button>
                )}
                <button
                  className="btn btn-outline-primary btn-sm rounded-pill px-4 fw-bold"
                  onClick={handleSyncEligibility}
                  disabled={tasks.syncEligibility.status === "SYNCING"}
                >
                  {tasks.syncEligibility.status === "SYNCING" ? "Syncing..." : "Sync Now"}
                </button>
              </div>
            </div>
          </div>

          {/* 6. Sync Data */}
          <div className="sync-item p-3">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-2">
              <div className="d-flex align-items-center gap-3">
                <div className="icon-box-lite">
                  <i className={`ti ti-refresh-dot ${tasks.syncData.status === "SYNCING" ? "spinning text-primary" : ""}`}></i>
                </div>
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <h6 className="fw-bold mb-0 text-dark">6. Sync Employee Reimbursement Data</h6>
                    {getStatusBadge(tasks.syncData.status)}
                  </div>
                  <small className="text-muted">Full data sync for claim histories and active balance ledgers.</small>
                </div>
              </div>
            </div>

            <div className="row align-items-center g-2 mt-2 pt-2 border-top">
              <div className="col-md-5">
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-light text-muted fw-medium" style={{ fontSize: "12px" }}>Emp ID</span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Employee ID e.g. 22088"
                    value={empIds.syncData}
                    onChange={(e) => setEmpIds((prev) => ({ ...prev, syncData: e.target.value }))}
                  />
                </div>
              </div>
              <div className="col-md-auto ms-auto d-flex gap-2">
                {tasks.syncData.responsePayload && (
                  <button
                    className="btn btn-outline-secondary btn-sm rounded-pill px-3"
                    onClick={() => setActiveResponseModal({ title: "Sync Data Response", data: tasks.syncData.responsePayload })}
                  >
                    Response
                  </button>
                )}
                <button
                  className="btn btn-outline-primary btn-sm rounded-pill px-4 fw-bold"
                  onClick={handleSyncData}
                  disabled={tasks.syncData.status === "SYNCING"}
                >
                  {tasks.syncData.status === "SYNCING" ? "Syncing..." : "Sync Now"}
                </button>
              </div>
            </div>
          </div>

          {/* 7. Sync Request By Reference Number */}
          <div className="sync-item p-3">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-2">
              <div className="d-flex align-items-center gap-3">
                <div className="icon-box-lite">
                  <i className={`ti ti-search ${tasks.syncRequestById.status === "SYNCING" ? "spinning text-primary" : ""}`}></i>
                </div>
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <h6 className="fw-bold mb-0 text-dark">7. Sync Request By Reference Number</h6>
                    {getStatusBadge(tasks.syncRequestById.status)}
                  </div>
                  <small className="text-muted">Fetch specific reimbursement claim details by Reference Number.</small>
                </div>
              </div>
            </div>

            <div className="row align-items-center g-2 mt-2 pt-2 border-top">
              <div className="col-md-4">
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-light text-muted fw-medium" style={{ fontSize: "12px" }}>Ref No *</span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g., 0000034600118"
                    value={referenceNumber}
                    onChange={(e) => setReferenceNumber(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-light text-muted fw-medium" style={{ fontSize: "12px" }}>Emp ID</span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Employee ID (Optional)"
                    value={empIds.syncRequestById}
                    onChange={(e) => setEmpIds((prev) => ({ ...prev, syncRequestById: e.target.value }))}
                  />
                </div>
              </div>
              <div className="col-md-auto ms-auto d-flex gap-2">
                {tasks.syncRequestById.responsePayload && (
                  <button
                    className="btn btn-outline-secondary btn-sm rounded-pill px-3"
                    onClick={() => setActiveResponseModal({ title: "Request By ID Response", data: tasks.syncRequestById.responsePayload })}
                  >
                    View Response
                  </button>
                )}
                <button
                  className="btn btn-outline-primary btn-sm rounded-pill px-4 fw-bold"
                  onClick={handleSyncRequestById}
                  disabled={tasks.syncRequestById.status === "SYNCING"}
                >
                  {tasks.syncRequestById.status === "SYNCING" ? "Syncing..." : "Fetch Request"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="p-3 rounded-3 bg-light border border-dashed text-center">
          <p className="small text-muted mb-0">
            <i className="ti ti-shield-check me-1 text-success"></i>
            Updates reflect immediately across the employee portal once synchronization is complete.
          </p>
        </div>
      </div>

      {/* Response Payload Modal */}
      {activeResponseModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} tabIndex={-1}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content border-0 shadow-sm">
              <div className="modal-header bg-light">
                <h5 className="modal-title fw-bold text-dark fs-15">
                  <i className="ti ti-code me-2 text-primary"></i>{activeResponseModal.title}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setActiveResponseModal(null)}
                ></button>
              </div>
              <div className="modal-body">
                <pre
                  className="bg-dark text-light p-3 rounded"
                  style={{ maxHeight: "350px", overflowY: "auto", fontSize: "12px" }}
                >
                  {JSON.stringify(activeResponseModal.data, null, 2)}
                </pre>
              </div>
              <div className="modal-footer bg-light">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm rounded-pill px-4"
                  onClick={() => setActiveResponseModal(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReimbursementSync;
